import type { MeResponse, OnboardingInvitationSummary } from '../../api/types';

/**
 * Is this signed-in person a client a firm invited straight to onboarding, with nowhere else to
 * go (crossmarc/steward-app#1764)? True only when ALL of these hold:
 *
 *   - no workspace membership and no pending workspace invitation (either of those is a real
 *     destination, and the ordinary routing handles it);
 *   - `canBootstrapTenant === false`, exactly: they cannot create an organization of their own.
 *     An unconsumed eligibility grant means they are a subscriber too, and the founder flow
 *     (`FirstRunGate`) wins. Absent (an older appcore) is not `false`;
 *   - at least one firm's onboarding invitation on `/me`.
 *
 * Such a person used to land on "create your workspace" or the workspace picker. Now they land
 * on `/onboarding` (`OnboardingClientGate.tsx`). The invitation list is a routing label: this
 * decides which screen to show, and nothing it shows grants access to anything.
 */
export function isOnboardingClient(me: MeResponse | undefined): boolean {
  if (!me) return false;
  return (me.memberships ?? []).length === 0
    && (me.invites ?? []).length === 0
    && me.canBootstrapTenant === false
    && (me.onboardingInvitations ?? []).length > 0;
}

/** The invitation the welcome page speaks for: the most recent one. */
export function currentOnboardingInvitation(me: MeResponse | undefined): OnboardingInvitationSummary | undefined {
  const all = [...(me?.onboardingInvitations ?? [])];
  all.sort((a, b) => b.invitedAt.localeCompare(a.invitedAt));
  return all[0];
}
